import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import OutageNotice from '@/components/OutageNotice';
import { OUTAGE_MODE } from '@/config/outage';

function formatApiError(detail) {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success('Welcome back!');
      if (data.user?.role === 'admin') navigate('/admin');
      else navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(formatApiError(detail));
      } else if (err.request && !err.response) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        setError(err.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    OUTAGE_MODE ? <OutageNotice context="login" /> :
    <div data-testid="login-page" className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Welcome Back</h1>
          <p className="text-slate-500 mt-2">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm space-y-5">
          {error && (
            <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm" data-testid="login-error">{error}</div>
          )}
          <div>
            <Label className="text-sm mb-1.5 block">Email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="rounded-xl" data-testid="login-email-input" required />
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Password</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Your password" className="rounded-xl" data-testid="login-password-input" required />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] py-3 font-medium" data-testid="login-submit-btn">
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
          <div className="text-center">
            <Link to="/forgot-password" className="text-sm text-[#56c6df] hover:underline" data-testid="forgot-password-link">Forgot Password?</Link>
          </div>
          <p className="text-center text-sm text-slate-500">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#df5681] hover:underline font-medium">Sign Up</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
